import { set, toJS } from 'mobx';
import { types, Instance, flow, getEnv } from 'mobx-state-tree';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import getCountryISO2 from 'country-iso-3-to-2';
import { AddressContract, AddressFormData } from '../../../../types/Address';
import { AddressVerificationContract } from '../../../../types/AddressValidation';
import { RootServices } from '../../../../types/RootStore';
import { DefaultValues } from '../../../../utils/constants';

const InputAddress = types
  .model({
    id: types.maybe(types.maybeNull(types.string)),
    firstName: types.maybe(types.maybeNull(types.string)),
    lastName: types.maybe(types.maybeNull(types.string)),
    address1: types.maybe(types.maybeNull(types.string)),
    address2: types.maybe(types.maybeNull(types.string)),
    city: types.maybe(types.maybeNull(types.string)),
    state: types.maybe(types.maybeNull(types.string)),
    postalCode: types.maybe(types.maybeNull(types.string)),
    country: types.maybe(types.maybeNull(types.string)),
    companyName: types.maybe(types.maybeNull(types.string)),
    phoneNumber: types.maybe(types.maybeNull(types.string)),
    residential: types.maybe(types.boolean),
  })
  .actions(self => {
    const clearAddress = (): void => {
      set(self, {
        address1: null,
        address2: null,
        city: null,
        state: null,
        country: null,
        postalCode: null,
      });
    };
    return {
      clearAddress,
    };
  });

const AddressVerificationResult = types
  .model({
    id: types.maybe(types.maybeNull(types.string)),
    firstName: types.maybe(types.maybeNull(types.string)),
    lastName: types.maybe(types.maybeNull(types.string)),
    address1: types.maybeNull(types.string),
    // ! address2 will always be null
    address2: types.maybeNull(types.string),
    city: types.maybeNull(types.string),
    state: types.maybeNull(types.string),
    country: types.maybeNull(types.string),
    postalCode: types.maybeNull(types.string),
    verificationStatus: types.maybeNull(
      types.union(
        types.literal('None'),
        types.literal('Verified'),
        types.literal('Partial'),
        types.literal('Ambiguous')
      )
    ),
    recommendations: types.array(types.string),
    companyName: types.maybe(types.maybeNull(types.string)),
    phoneNumber: types.maybe(types.maybeNull(types.string)),
    residential: types.maybe(types.maybeNull(types.boolean)),
  })
  .views(self => ({
    get isPartiallyVerified(): boolean {
      return self.verificationStatus === 'Partial';
    },
    get isVerified(): boolean {
      return self.verificationStatus === 'Verified';
    },
    get isUnverified(): boolean {
      return (
        self.verificationStatus === 'None' ||
        self.verificationStatus === 'Ambiguous'
      );
    },
  }))
  .actions(self => {
    const clearResult = (): void => {
      set(self, {
        address1: null,
        address2: null,
        city: null,
        state: null,
        country: null,
        postalCode: null,
        verificationStatus: null,
        recommendations: [],
      });
    };
    return {
      clearResult,
    };
  });

const DirectComparisonFields = ['city', 'state', 'country'];

export const MISSING_ADDRESS2_INDICATOR = 'Missing Secondary Information';
export const UNRECOGNIZED_ADDRESS2_INDICATOR =
  'Secondary Information Unrecognized';

export const AddressVerificationModel = types
  .model({
    customerAddress: types.optional(InputAddress, {}),
    verificationAddress: types.optional(AddressVerificationResult, {}),
    cachedAddress: types.optional(InputAddress, {}),
  })
  .volatile(() => ({
    isWorking: false,
    isEnabled: false,
  }))
  .views(self => {
    const inputMatchesVerifiedAddress = (): boolean => {
      const customerAddress = toJS(self.customerAddress);
      const verifiedAddress = toJS(self.verificationAddress);
      const expectedStreetAddress = customerAddress.address2
        ? `${customerAddress.address1} ${customerAddress.address2}`
        : customerAddress.address1;
      const streetAddressMatches =
        verifiedAddress.address1 === expectedStreetAddress;

      const isUSAddress =
        customerAddress.country === DefaultValues.DEFAULT_SHIPPING_COUNTRY;
      const postalCodeMatches = isUSAddress
        ? customerAddress.postalCode?.substr(0, 5) ===
          verifiedAddress.postalCode?.substr(0, 5)
        : customerAddress.postalCode === verifiedAddress.postalCode;

      const otherFieldsMatch = isEqual(
        pick(customerAddress, DirectComparisonFields),
        pick(verifiedAddress, DirectComparisonFields)
      );
      return streetAddressMatches && postalCodeMatches && otherFieldsMatch;
    };
    return {
      get missingAddress2(): boolean {
        if (!self.isEnabled) return false;
        return (
          self.verificationAddress.isPartiallyVerified &&
          self.verificationAddress.recommendations?.includes(
            MISSING_ADDRESS2_INDICATOR
          )
        );
      },
      get unrecognizedAddress2(): boolean {
        if (!self.isEnabled) return false;
        return (
          self.verificationAddress.isPartiallyVerified &&
          self.verificationAddress.recommendations?.includes(
            UNRECOGNIZED_ADDRESS2_INDICATOR
          )
        );
      },
      get partiallyVerifiedNoRecommendations(): boolean {
        if (!self.isEnabled) return false;
        return (
          self.verificationAddress.isPartiallyVerified &&
          self.verificationAddress.recommendations.length === 0
        );
      },
      get completelyVerifiedAddress(): boolean {
        if (!self.isEnabled) return false;
        return (
          self.verificationAddress.isVerified && inputMatchesVerifiedAddress()
        );
      },
      get basicVerifiedAddress(): boolean {
        if (!self.isEnabled) return false;
        return (
          self.verificationAddress.isVerified && !inputMatchesVerifiedAddress()
        );
      },
      get unverifiedAddress(): boolean {
        if (!self.isEnabled) return false;
        return self.verificationAddress.isUnverified;
      },
      get verificationAddressSummary(): AddressFormData {
        const verifiedAddress = toJS(self.verificationAddress);
        return {
          firstName: verifiedAddress.firstName ?? undefined,
          lastName: verifiedAddress.lastName ?? undefined,
          address1: verifiedAddress.address1 ?? undefined,
          city: verifiedAddress.city ?? undefined,
          country: verifiedAddress.country ?? undefined,
          postalCode: verifiedAddress.postalCode ?? undefined,
          state: verifiedAddress.state ?? undefined,
          phoneNumber: verifiedAddress.phoneNumber ?? undefined,
          residential: verifiedAddress.residential ?? undefined,
        };
      },
      get customerAddressSummary(): AddressFormData {
        const customerAddress = toJS(self.customerAddress);
        return {
          firstName: customerAddress.firstName ?? undefined,
          lastName: customerAddress.lastName ?? undefined,
          address1: customerAddress.address1 ?? undefined,
          address2: customerAddress.address2 ?? undefined,
          city: customerAddress.city ?? undefined,
          country: customerAddress.country ?? undefined,
          postalCode: customerAddress.postalCode ?? undefined,
          state: customerAddress.state ?? undefined,
          phoneNumber: customerAddress.phoneNumber ?? undefined,
          residential: customerAddress.residential,
        };
      },
    };
  })
  .actions(self => {
    const { addressVerificationService, isoCodeService } =
      getEnv<RootServices>(self);

    const setIsEnabled = (isEnabled = true): void => {
      set(self, { isEnabled });
    };

    const setIsWorking = (isWorking = true): void => {
      set(self, { isWorking });
    };

    const setCustomerAddress = (address: AddressContract): void => {
      set(self.customerAddress, address);
    };

    const setCachedAddress = (address: AddressContract): void => {
      set(self.cachedAddress, address);
    };

    const setVerificationAddress = (
      response: AddressVerificationContract
    ): void => {
      set(self, {
        verificationAddress: response,
      });
    };

    const clearVerificationAddress = (): void => {
      self.verificationAddress.clearResult();
    };

    const canAddressBeVerified = (address: AddressContract): boolean => {
      return (
        !!address.address1 &&
        !!address.country &&
        !!address.state &&
        !!address.city
      );
    };

    const isCachedAddress = (address: AddressContract): boolean => {
      if (!self.cachedAddress) return false;

      return isEqual(address, self.cachedAddress);
    };

    const verifyAddressFlow = flow(function* (address: AddressContract) {
      if (!self.isEnabled) return;
      setIsWorking();
      if (!canAddressBeVerified(address)) {
        clearVerificationAddress();
        setIsWorking(false);
        return;
      }

      if (isCachedAddress(address)) {
        setIsWorking(false);
        return;
      }

      setCustomerAddress(address);
      const adjustedAddress = {
        ...address,
        country: yield isoCodeService.getIsoCodeFromName(
          address.country as string
        ),
      };

      const result = yield addressVerificationService.verify(adjustedAddress);
      if (!result) {
        self.cachedAddress.clearAddress();
        clearVerificationAddress();
        setIsWorking(false);
        return;
      }
      const countryCode =
        getCountryISO2(result.country.toUpperCase()) || result.country;

      setVerificationAddress({
        ...address,
        ...result,
        address2: null,
        country: yield isoCodeService.getNameFromIsoCode(countryCode),
      });
      setCachedAddress(address);
      setIsWorking(false);
    });

    return {
      setIsEnabled,
      verifyAddressFlow,
      clearVerificationAddress,
    };
  });

export interface IAddressVerification
  extends Instance<typeof AddressVerificationModel> {}
